import { LogViewer } from "@/components/LogViewer";
import { CopyAllLogsButton } from "@/components/log-components/CopyAllLogsButton";
import { useEnvironment } from "@/queries/environment-queries";
import { useMemo, type FC } from "react";
import { useParams } from "react-router-dom";
import { EnvironmentDetailsHeader } from "@/components/environments/EnvironmentDetailsHeader";
import { BackButton } from "@/components/environments/BackButton";
import { environmentLogsUrl } from "@/utils/environment";
import { IconBox } from "@/assets/icons/geist/IconBox";
import { useEnvironmentDetailsOutletContext } from "@/hooks/use-environment-details-outlet-context";
import { useStreamLogs } from "@/hooks/use-stream-logs";

export const EnvironmentLogsPage: FC = () => {
    const { environmentId } = useParams();
    // Title is handled by EnvironmentDetailsLayout using dynamic title

    const { data: environment, error } = useEnvironment(environmentId);
    const outletContext = useEnvironmentDetailsOutletContext();
    const showingAgentConversation = outletContext?.showingAgentConversation ?? false;

    const logsUrl = useMemo(() => environmentLogsUrl(environment), [environment]);
    const {
        logGroups,
        error: logsError,
        loadingMessage,
    } = useStreamLogs({ environment, logsUrl, maxLinesPerGroup: 10000 });

    if (error) {
        return (
            <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                <IconBox state="open" className="mb-10 h-16 w-16 shrink-0 animate-rotateBox text-content-primary" />
                <h1 className="mb-2 text-xl font-semibold text-content-primary">
                    That environment doesn&#x2019;t exist
                </h1>
                <p className="text-lg text-content-secondary">You can always create a new one.</p>
            </div>
        );
    }

    return (
        <div className="flex size-full flex-col">
            {environmentId && (
                <div className="flex flex-col gap-4">
                    {!showingAgentConversation && <EnvironmentDetailsHeader showActionBar={false} />}
                    <div className="flex justify-between pb-4">
                        <BackButton />
                        {logGroups && Object.keys(logGroups).length > 0 && (
                            <CopyAllLogsButton
                                logGroups={logGroups}
                                trackingId="copy-all-logs-environment-logs"
                                className="text-content-primary hover:text-content-primary"
                            />
                        )}
                    </div>
                </div>
            )}
            <LogViewer
                logGroups={logGroups}
                error={logsError}
                loadingMessage={loadingMessage}
                // We're checking for timestamps here as a way to filter out log lines that supervisor is not
                // intentionally emitting. This allows us to clean up the log output even for legacy runners
                filterLogLinesWithoutTimestamps={true}
            />
        </div>
    );
};
